import { useConversationStore } from "../store/conversationStore";
import { useConversationsStore } from "../store/conversationsStore";
import {
  type MessagingMessage,
  MessagingParticipantType,
} from "@/model/messaging";
import type { InitConversationChatStoreOptions } from "@/modules/conversation/ConversationChatStore";
import { useCallbackRef } from "@radix-ui/react-use-callback-ref";
import { cloneDeep } from "lodash";
import { map, tap } from "rxjs";

const SYSTEM_FIELD_KEY = "system.field";
const SYSTEM_FIELD_VISITOR_NAME = "visitor.name";
const SYSTEM_FIELD_VISITOR_EMAIL = "visitor.email";

export const useInteractions = () => {
  const {
    bind: { listMessages, submitForm, sendMessageFeedback },
  } = useConversationStore();

  const {
    use: { participants, deployment, conversationUid },
    bind: { setParticipant, buildDefaultMessage, batchSetMessageStatus },
  } = useConversationsStore();

  const handleSubmitForm = useCallbackRef<
    NonNullable<InitConversationChatStoreOptions["submitForm"]>
  >((args) => {
    const { messageUid, formValues: formData } = args;

    return submitForm(messageUid, formData).pipe(
      map(({ taskId }) => ({ taskUid: taskId })),
      tap(() => {
        if (!deployment?.uid) {
          return;
        }
        const visitor = cloneDeep(participants);
        for (const formValue of Object.values(formData)) {
          const systemField = formValue.metadata?.[SYSTEM_FIELD_KEY];
          if (systemField === SYSTEM_FIELD_VISITOR_NAME) {
            visitor[deployment.uid].name = formValue.value as string;
          }
          if (systemField === SYSTEM_FIELD_VISITOR_EMAIL) {
            visitor[deployment.uid].email = formValue.value as string;
          }
        }
        setParticipant(visitor[deployment.uid]);
      })
    );
  });

  const handleLoadMessages = useCallbackRef<
    NonNullable<InitConversationChatStoreOptions["loadMessages"]>
  >(({ page, pageSize }) => {
    return listMessages({ page, pageSize }).pipe(
      map(({ messages, total }) => {
        return {
          messages: (messages?.reverse() ?? []) as MessagingMessage[],
          total: total ?? 0,
        };
      })
    );
  });

  const handleCreateNewSendMessage = useCallbackRef<
    NonNullable<InitConversationChatStoreOptions["createNewSendMessage"]>
  >(() => {
    if (!conversationUid) {
      throw new Error("conversationUid is required");
    }
    return buildDefaultMessage(conversationUid);
  });

  const getMessageBubblePosition = useCallbackRef(
    (message: MessagingMessage) =>
      message.participant.type === MessagingParticipantType.Visitor
        ? "sent"
        : "received"
  );

  const handleSendMessageFeedback = useCallbackRef<
    NonNullable<InitConversationChatStoreOptions["sendMessageFeedback"]>
  >(({ messageUid, feedbackType }) => {
    return sendMessageFeedback(messageUid, feedbackType).pipe(
      map((message) => ({ message }))
    );
  });

  const handleBatchSetMessageStatus = useCallbackRef<
    NonNullable<InitConversationChatStoreOptions["batchSetMessageStatus"]>
  >(({ deliveredStatus, messageUids }) => {
    return batchSetMessageStatus({ deliveredStatus, messageUids });
  });

  return {
    getMessageBubblePosition,
    handleLoadMessages,
    handleCreateNewSendMessage,
    handleSubmitForm,
    handleSendMessageFeedback,
    handleBatchSetMessageStatus,
  };
};
